import type { ReactElement } from "react"
import styled from "styled-components"
import { MaterialRail } from "~/features/studyEbook/components/MaterialRail"
import type { StudyMaterial } from "~/features/studyEbook/types"
import { BackButton, Skeleton, StateMessage } from "~/shared/ui"
import type { DetailView } from "../types"
import { DetailHero } from "./DetailHero"
import { SessionRail } from "./SessionRail"

const SKELETON_KEYS = ["a", "b", "c"] as const

/**
 * Wraps every branch, so the way out of a detail screen is there whether the
 * screen loaded or not — a customer who lands on a study that failed to load
 * needs it more than one looking at the hero, not less.
 */
const Frame = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`

const SkeletonRail = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing.xs};
  width: ${({ theme }) => theme.detail.sessionCardWidth};
`

interface DetailPageProps {
  readonly view: DetailView | undefined
  readonly isLoading: boolean
  readonly isError: boolean
  readonly sessionsTitle: string
  readonly emptySessionsMessage: string
  readonly errorMessage: string
  readonly notFoundMessage: string
  /**
   * The book accompanying this asset, when one resolved. Studies only today —
   * the event screen shares this shell and passes nothing.
   */
  readonly material?: StudyMaterial | undefined
  /** Prefix for every `data-qa-hook` on the page, e.g. `study-detail`. */
  readonly qaHook: string
}

/**
 * Everything below the back control: the screen itself, in whichever state it is
 * in. Split out so each state stays an early return rather than a branch nested
 * inside the frame's JSX.
 */
const DetailBody = ({
  view,
  isLoading,
  isError,
  sessionsTitle,
  emptySessionsMessage,
  errorMessage,
  notFoundMessage,
  material,
  qaHook,
}: DetailPageProps): ReactElement => {
  if (isLoading) {
    return (
      <Page data-qa-hook={`${qaHook}-loading`}>
        <Skeleton $height="20rem" />
        <SkeletonRail>
          {SKELETON_KEYS.map(key => (
            <SkeletonCard key={key}>
              <Skeleton $height="11rem" />
              <Skeleton />
            </SkeletonCard>
          ))}
        </SkeletonRail>
      </Page>
    )
  }

  if (isError) {
    return (
      <StateMessage tone="error" qaHook={`${qaHook}-error`}>
        {errorMessage}
      </StateMessage>
    )
  }

  if (!view) {
    return <StateMessage qaHook={`${qaHook}-not-found`}>{notFoundMessage}</StateMessage>
  }

  return (
    <Page data-qa-hook={qaHook}>
      <DetailHero
        title={view.title}
        metaLine={view.metaLine}
        description={view.description}
        backdropUrl={view.backdropUrl}
        startHref={view.startHref}
        hasProgress={view.hasProgress}
        material={material}
        qaHook={`${qaHook}-hero`}
      />
      <SessionRail
        title={sessionsTitle}
        sessions={view.sessions}
        emptyMessage={emptySessionsMessage}
        qaHook={`${qaHook}-sessions`}
      />
      {material ? <MaterialRail title="Study Materials" material={material} qaHook={`${qaHook}-materials`} /> : null}
    </Page>
  )
}

/**
 * The shared shell for the study and digital-event detail screens. Figma only
 * draws the loaded state, so the loading, empty, error, and not-found branches
 * are decisions made here rather than in the design.
 */
export const DetailPage = (props: DetailPageProps): ReactElement => (
  <Frame>
    {/* The library is the only place a detail screen is reached from, so the
        destination is fixed rather than the previous entry in history: a customer
        who arrived on a shared link has no previous entry to go back to. */}
    <BackButton to="/" label="Back to library" qaHook={`${props.qaHook}-back`} />
    <DetailBody {...props} />
  </Frame>
)
